import { API_CONFIG } from '../../environments/api-config';
import { I18nManager } from './i18n';

interface TurnstileApi {
  render(
    container: HTMLElement | string,
    options: {
      sitekey: string;
      action?: string;
      appearance?: 'always' | 'execute' | 'interaction-only';
      execution?: 'render' | 'execute';
      'refresh-expired'?: 'auto' | 'manual' | 'never';
      callback?: (token: string) => void;
      'error-callback'?: (code: string) => void;
      'expired-callback'?: () => void;
      'before-interactive-callback'?: () => void;
      'after-interactive-callback'?: () => void;
    },
  ): string | null | undefined;
  execute(widgetId: string): void;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export class ContactFormManager {
  private formEl: HTMLFormElement | null = null;
  private turnstileContainerEl: HTMLElement | null = null;
  private turnstileWidgetId: string | null = null;
  private turnstilePending: { resolve: (token: string) => void; reject: (err: Error) => void } | null = null;
  private i18n: I18nManager;

  constructor(i18n: I18nManager) {
    this.i18n = i18n;
    this.initForm();
    this.initTurnstile();
  }

  private initForm(): void {
    this.formEl = document.getElementById('contact-form') as HTMLFormElement;
    if (!this.formEl) return;

    this.turnstileContainerEl = document.getElementById('contact-turnstile-container');

    this.formEl.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.handleSubmit();
    });
  }

  private initTurnstile(): void {
    if (typeof window === 'undefined' || !window.turnstile) return;
    const turnstileEl = document.getElementById('contact-turnstile');
    if (!turnstileEl || this.turnstileWidgetId !== null) return;

    const widgetId = window.turnstile.render(turnstileEl, {
      sitekey: API_CONFIG.turnstileSiteKey,
      action: 'contact_form',
      appearance: 'interaction-only',
      execution: 'execute',
      'refresh-expired': 'manual',
      callback: (token: string) => {
        const pending = this.turnstilePending;
        this.turnstilePending = null;
        if (pending) pending.resolve(token);
      },
      'error-callback': (code: string) => {
        const pending = this.turnstilePending;
        this.turnstilePending = null;
        if (pending) pending.reject(new Error(`Turnstile verification error (${code})`));
      },
      'expired-callback': () => {
        this.resetTurnstile();
        const pending = this.turnstilePending;
        this.turnstilePending = null;
        if (pending) pending.reject(new Error('Turnstile token expired'));
      },
      'before-interactive-callback': () => {
        if (this.turnstileContainerEl) {
          this.turnstileContainerEl.classList.remove('hidden');
        }
      },
      'after-interactive-callback': () => {
        if (this.turnstileContainerEl) {
          this.turnstileContainerEl.classList.add('hidden');
        }
      },
    });

    this.turnstileWidgetId = widgetId ?? null;
  }

  private resetTurnstile(): void {
    if (this.turnstileWidgetId && typeof window !== 'undefined' && window.turnstile) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
    if (this.turnstileContainerEl) {
      this.turnstileContainerEl.classList.add('hidden');
    }
  }

  private async ensureTurnstileApi(): Promise<TurnstileApi | null> {
    if (typeof window === 'undefined') return null;
    if (window.turnstile) return window.turnstile;
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 100));
      if (window.turnstile) return window.turnstile;
    }
    return null;
  }

  private async obtainTurnstileToken(): Promise<string> {
    const api = await this.ensureTurnstileApi();
    if (!api) {
      throw new Error('Turnstile widget is not available');
    }
    this.initTurnstile();
    if (!this.turnstileWidgetId) {
      throw new Error('Turnstile widget failed to render');
    }

    return new Promise<string>((resolve, reject) => {
      this.turnstilePending = { resolve, reject };
      api.execute(this.turnstileWidgetId!);
    });
  }

  private async handleSubmit(): Promise<void> {
    if (!this.formEl) return;

    const dict = this.i18n.getTranslations();
    const nameInput = this.formEl.querySelector<HTMLInputElement>('#contact-name');
    const emailInput = this.formEl.querySelector<HTMLInputElement>('#contact-email');
    const companyInput = this.formEl.querySelector<HTMLInputElement>('#contact-company');
    const subjectInput = this.formEl.querySelector<HTMLInputElement>('#contact-subject');
    const messageInput = this.formEl.querySelector<HTMLTextAreaElement>('#contact-message');
    const honeypotInput = this.formEl.querySelector<HTMLInputElement>('#contact-hp-field');
    const submitBtn = this.formEl.querySelector<HTMLButtonElement>('#contact-submit');

    // Client-side validation
    let valid = true;
    [nameInput, emailInput, subjectInput, messageInput].forEach((input) => {
      if (input) {
        if (!input.value.trim()) {
          input.classList.add('border-danger');
          valid = false;
        } else {
          input.classList.remove('border-danger');
        }
      }
    });

    if (emailInput && emailInput.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailInput.value.trim())) {
        emailInput.classList.add('border-danger');
        valid = false;
      }
    }

    if (!valid) return;

    this.setSubmitting(submitBtn, dict.contact.submitting);

    let turnstileToken: string;
    try {
      turnstileToken = await this.obtainTurnstileToken();
    } catch {
      this.resetTurnstile();
      this.showNotification('error', dict.contact.errorTitle, dict.contact.errorDesc);
      this.restoreSubmit(submitBtn, dict.contact.submit);
      return;
    }

    const payload = {
      name: nameInput?.value.trim() ?? '',
      email: emailInput?.value.trim() ?? '',
      company: companyInput?.value.trim() ?? '',
      subject: subjectInput?.value.trim() ?? '',
      message: messageInput?.value.trim() ?? '',
      website_url: honeypotInput?.value ?? '',
      turnstileToken,
    };

    try {
      const response = await fetch(API_CONFIG.contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        this.formEl.reset();
        this.resetTurnstile();
        this.showNotification('success', dict.contact.successTitle, dict.contact.successDesc);
      } else {
        this.resetTurnstile();
        this.showNotification('error', dict.contact.errorTitle, dict.contact.errorDesc);
      }
    } catch {
      this.resetTurnstile();
      this.showNotification('error', dict.contact.errorTitle, dict.contact.errorDesc);
    } finally {
      this.restoreSubmit(submitBtn, dict.contact.submit);
    }
  }

  private setSubmitting(submitBtn: HTMLButtonElement | null, submittingLabel: string): void {
    if (!submitBtn) return;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `
      <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      ${submittingLabel}
    `;
  }

  private restoreSubmit(submitBtn: HTMLButtonElement | null, submitLabel: string): void {
    if (!submitBtn) return;
    submitBtn.disabled = false;
    submitBtn.textContent = submitLabel;
  }

  private showNotification(kind: 'success' | 'error', title: string, desc: string): void {
    let toast = document.getElementById('contact-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'contact-toast';
      document.body.appendChild(toast);
    }

    const borderClass = kind === 'success' ? 'border-l-success' : 'border-l-danger';
    const iconBoxClass = kind === 'success' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger';
    toast.className = `fixed bottom-6 right-6 z-50 p-6 glass-card border-l-4 ${borderClass} max-w-md shadow-2xl transition-all duration-300 transform translate-y-10 opacity-0`;

    const icon =
      kind === 'success'
        ? '<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>'
        : '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>';

    toast.innerHTML = `
      <div class="flex items-start gap-4">
        <div class="p-2 rounded-lg ${iconBoxClass}">
          <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">${icon}</svg>
        </div>
        <div>
          <h4 class="font-bold text-text-primary text-base">${title}</h4>
          <p class="text-xs text-text-secondary mt-1">${desc}</p>
        </div>
      </div>
    `;

    requestAnimationFrame(() => {
      toast?.classList.remove('translate-y-10', 'opacity-0');
    });

    setTimeout(() => {
      toast?.classList.add('translate-y-10', 'opacity-0');
    }, 5000);
  }
}
